/**
 * SafetyTabbedPanel - Panel izquierdo con pestañas para módulo de seguridad
 *
 * @version 1.0
 * @module core/modules/safety
 *
 * @description
 * Pestañas: Cámaras, Alarmas, Puntos de emergencia, Comisarías, Bomberos, Defensa civil
 */

import { useState } from 'react';
import {
  Camera, Bell, AlertTriangle, Shield, Flame, LifeBuoy,
} from 'lucide-react';
import { BaseInventoryTab } from '../../layouts/panels/LeftPanel/BaseInventoryTab';
import { LeftPanelToggle } from '../../layouts/panels/LeftPanel/LeftPanel';
import { useRightPanel } from '../../layouts/panels/RightPanel/RightPanelContext';
import { useMapTools } from '../../map-tools';
import { useSafetyElementColorsStore } from './store/element-colors-store';
import { MAPPABLE_ELEMENT_IDS } from './constants/element-colors';
import type { SafetyElementId } from './constants/element-colors';
import '../../layouts/panels/LeftPanel/tabbed-left-panel.css';

type SafetyTabType =
  | 'cameras'
  | 'alarms'
  | 'emergency'
  | 'police'
  | 'fire-stations'
  | 'civil-defense';

interface TabInfo {
  id: SafetyTabType;
  icon: React.ReactNode;
  tooltip: string;
  title: string;
  emptyMessage: string;
}

const TABS: TabInfo[] = [
  { id: 'cameras',        icon: <Camera size={16} />,        tooltip: 'Cámaras de vigilancia', title: 'Cámaras',           emptyMessage: 'Sin cámaras registradas' },
  { id: 'alarms',         icon: <Bell size={16} />,          tooltip: 'Alarmas comunitarias',   title: 'Alarmas',           emptyMessage: 'Sin alarmas registradas' },
  { id: 'emergency',      icon: <AlertTriangle size={16} />, tooltip: 'Puntos de emergencia',   title: 'Emergencias',       emptyMessage: 'Sin puntos de emergencia' },
  { id: 'police',         icon: <Shield size={16} />,        tooltip: 'Comisarías',             title: 'Comisarías',        emptyMessage: 'Sin comisarías registradas' },
  { id: 'fire-stations',  icon: <Flame size={16} />,         tooltip: 'Estaciones de bomberos', title: 'Bomberos',          emptyMessage: 'Sin estaciones de bomberos' },
  { id: 'civil-defense',  icon: <LifeBuoy size={16} />,      tooltip: 'Defensa civil',          title: 'Defensa civil',     emptyMessage: 'Sin centros de defensa civil' },
];

export function SafetyTabbedPanel(): JSX.Element {
  const [activeTab, setActiveTab] = useState<SafetyTabType>('cameras');
  const { canChangeContent } = useRightPanel();
  const { selectElement, getSelectedElement } = useMapTools();
  const getColor = useSafetyElementColorsStore((s) => s.getColor);
  const setColor = useSafetyElementColorsStore((s) => s.setColor);

  const activeTabInfo = TABS.find(t => t.id === activeTab)!;
  const isMappable = MAPPABLE_ELEMENT_IDS.has(activeTab);
  const selectionKey = `safety-${activeTab}`;
  const activeElementId = getSelectedElement(selectionKey);

  const handleCodeClick = async (elementId: string): Promise<void> => {
    console.log(`[SafetyTabbedPanel] Elemento clickeado: ${activeTab} - ${elementId}`);
    const canChange = await canChangeContent();
    if (!canChange) return;

    // Marcar elemento como seleccionado para resaltado en sidebar
    selectElement(selectionKey, elementId);

    console.log(`[SafetyTabbedPanel] Despachando evento: safety-${activeTab}-select`);
    window.dispatchEvent(new CustomEvent(`safety-${activeTab}-select`, {
      detail: { elementId, elementType: activeTab },
    }));
  };

  return (
    <div className="tabbed-left-panel">
      <div className="tabs-header">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
            title={tab.tooltip}
            aria-label={tab.tooltip}
          >
            {tab.icon}
          </button>
        ))}
      </div>

      <div className="content-card-wrapper">
        <div className="content-card">
          <div className="tab-content">
            <BaseInventoryTab
              key={activeTab}
              title={activeTabInfo.title}
              emptyMessage={activeTabInfo.emptyMessage}
              eventPrefix={`safety-${activeTab}`}
              moduleId="safety"
              elementColor={isMappable ? getColor(activeTab as SafetyElementId) : undefined}
              onElementColorChange={isMappable ? (hex) => setColor(activeTab as SafetyElementId, hex) : undefined}
              onCodeClick={handleCodeClick}
              activeElementId={activeElementId}
            />
          </div>
          <LeftPanelToggle />
        </div>
      </div>
    </div>
  );
}
